import React, { useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';
import { TbFileExport } from 'react-icons/tb';

import {
  ApolloError,
  Button,
  HStack,
  Icon,
  IconButton,
  LoadingIndicator,
  NavPersistedScrollPane as ScrollPane,
  SearchInput,
  Spacer,
  Text,
  Tooltip,
  VStack,
} from '../../core-ui';
import {
  GetVendors,
  GetVendorsVariables,
  GET_VENDORS,
} from '../../queries/billing';
import { APP_STATUSES } from '../../support/constants/AppStatuses';
import { useAuthState } from '../AuthState';
import { BillingVendorTable } from '../Billing/BillingVendorTable';
import { useClearBilling } from '../Billing/clearVendorBilling';
import VendorBillingExportDialog from '../Billing/VendorBillingExportDialog';
import { useNavigation, useNavPersistedState } from '../Navigation';
import { useSeason } from '../SeasonState';
import { FilterDropdown } from '../Vendors/FilterDropdown';
import { filterVendors } from '../Vendors/filterVendors';
import { getDefaultVendorStatusSelection } from '../Vendors/getDefaultVendorStatusSelection';
import { getCategoryOptionsFromGraphQLFields } from '../../support/helpers/CategoryOptions';
import { toMap } from '../../support/helpers/toMap';

export function BillingVendorListPage() {
  const { account } = useAuthState();
  const { season } = useSeason();
  const { navigate } = useNavigation();
  const clearBilling = useClearBilling();

  const allowClearBilling = account.system_admin ?? false;
  // Currently the only reason to allow selection is for clear billing
  const allowSelection = allowClearBilling;

  const [selectedVendors, setSelectedVendors] = useNavPersistedState(
    'selected-vendors',
    new Set<string>(),
  );
  const [searchText, setSearchText] = useNavPersistedState('search-value', '');
  const [selectedCategories, setSelectedCategories] = useNavPersistedState(
    'selected-categories',
    new Set<string>(),
  );
  const [selectedStatuses, setSelectedStatuses] = useNavPersistedState(
    'selected-statuses',
    getDefaultVendorStatusSelection(),
  );
  const [selectedTags, setSelectedTags] = useNavPersistedState(
    'selected-tags',
    new Set<string>(),
  );

  const [showExportDialog, setShowExportDialog] = useState(false);

  const { data, error, refetch } = useQuery<GetVendors, GetVendorsVariables>(
    GET_VENDORS,
    {
      variables: { seasonId: season?.id ?? '' },
      fetchPolicy: 'cache-and-network',
    },
  );

  const vendors = data?.season?.vendors ?? [];
  const totalVendors = vendors.length;
  const options = getCategoryOptionsFromGraphQLFields(
    data?.season?.signupForm?.fields ?? [],
  );
  const optionsMap = toMap(options);

  const filteredVendors = useMemo(() => {
    const vendors = data?.season?.vendors ?? [];
    return filterVendors({
      vendors,
      filters: {
        searchText,
        selectedCategories,
        selectedStatuses,
        selectedTags,
      },
      optionsMap,
    });
  }, [
    data?.season?.vendors,
    optionsMap,
    searchText,
    selectedCategories,
    selectedStatuses,
    selectedTags,
  ]);

  const tags = data?.season?.tags ?? [];
  const vendorCategories = data?.season?.vendorCategories ?? [];
  const markets = data?.season?.markets ?? [];

  if (error) {
    return <ApolloError error={error} />;
  }

  const isLoading = !data;
  const currency = data?.myAccount.settings.currency ?? 'USD';

  return (
    <VStack px={10} pt={10} flex={1} minH={0} alignItems="stretch" spacing={2}>
      <HStack>
        <SearchInput
          value={searchText}
          onChange={(event) => {
            setSearchText(event.target.value);
          }}
          onClear={() => {
            setSearchText('');
          }}
        />
        <FilterDropdown
          categories={vendorCategories}
          selectedCategories={selectedCategories}
          statuses={APP_STATUSES}
          selectedStatuses={selectedStatuses}
          tags={tags}
          selectedTags={selectedTags}
          onApply={({ categories, statuses, tags }) => {
            setSelectedCategories(categories);
            setSelectedStatuses(statuses);
            setSelectedTags(tags);
          }}
          onReset={() => {
            setSelectedCategories(new Set());
            setSelectedStatuses(getDefaultVendorStatusSelection());
            setSelectedTags(new Set());
          }}
        />
        {filteredVendors.length === totalVendors ? null : (
          <Text>
            {t('Showing {showing} of {total} vendor(s)', {
              showing: filteredVendors.length,
              total: totalVendors,
            })}
          </Text>
        )}
        <Spacer />
        {selectedVendors.size > 0 ? (
          <Text>
            {t('{count} vendor(s) selected', { count: selectedVendors.size })}
          </Text>
        ) : null}
        {allowClearBilling && selectedVendors.size > 0 ? (
          <Button
            onClick={async () => {
              await clearBilling(selectedVendors);
              setSelectedVendors(new Set());
              void refetch();
            }}
          >
            {t('Clear Billing')}
          </Button>
        ) : null}
        <Tooltip label={t('Export')} placement="top">
          <IconButton
            aria-label={t('Export')}
            icon={<Icon as={TbFileExport} fontSize={20} />}
            onClick={() => setShowExportDialog(true)}
          />
        </Tooltip>
      </HStack>
      <ScrollPane
        storeKey="vendor-list"
        contentContainerProps={{ flexGrow: 1, pb: 10 }}
      >
        {isLoading ? (
          <LoadingIndicator />
        ) : (
          <BillingVendorTable
            currency={currency}
            optionsMap={optionsMap}
            vendors={filteredVendors}
            selected={selectedVendors}
            onSelectionChange={allowSelection ? setSelectedVendors : undefined}
            onRowClick={(vendor) => {
              navigate('/billing/vendors/:id', { id: vendor.id });
            }}
          />
        )}
      </ScrollPane>
      {showExportDialog ? (
        <VendorBillingExportDialog
          markets={markets}
          onDismiss={() => {
            setShowExportDialog(false);
          }}
          tags={tags}
        />
      ) : null}
    </VStack>
  );
}
